/* TGGCore: Gift Finder — guided quiz that auto-advances on each pick and, on the
   final pick, navigates straight to the resolved collection (no Next button, no
   result screen). Vanilla IIFE, document-delegated events (survives theme
   section reloads and InstantClick page swaps). State is per root element so
   multiple finders can live on one page. Markup contract:
   snippets/tgg-gift-finder.liquid emits [data-gf-root] with [data-gf-opt] chips
   (incl. a [data-gf-any] "no preference" chip), [data-gf-back], [data-gf-step],
   [data-gf-progress*], [data-gf-status], and a <script data-gf-combos> JSON map
   "recipHandle|occHandle" -> {u,c,t}. */
(function () {
  'use strict';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function parseCombos(root) {
    var el = root.querySelector('[data-gf-combos]');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  function getState(root) {
    if (!root.__gfState) {
      root.__gfState = {
        step: 1,
        totalSteps: parseInt(root.getAttribute('data-gf-total-steps'), 10) || 3,
        priority: root.getAttribute('data-gf-priority') || 'occasion',
        gteParam: root.getAttribute('data-gf-gte-param') || 'filter.v.price.gte',
        lteParam: root.getAttribute('data-gf-lte-param') || 'filter.v.price.lte',
        combos: parseCombos(root),
        sel: { recipient: null, occasion: null, budget: null }
      };
    }
    return root.__gfState;
  }

  function stepEls(root) { return root.querySelectorAll('[data-gf-step]'); }
  function stepEl(root, id) { return root.querySelector('[data-gf-step="' + id + '"]'); }

  /* ---- Selection -> auto-advance ---- */
  function selectChip(root, chip) {
    var axis = chip.getAttribute('data-gf-opt');
    if (!axis) return;
    var step = chip.closest('.gf__step');
    if (step) {
      var group = step.querySelectorAll('[data-gf-opt="' + axis + '"]');
      for (var i = 0; i < group.length; i++) group[i].setAttribute('aria-pressed', 'false');
    }
    var state = getState(root);
    if (chip.hasAttribute('data-gf-any')) {
      state.sel[axis] = null;
    } else {
      state.sel[axis] = {
        value: chip.getAttribute('data-gf-value') || '',
        url: chip.getAttribute('data-gf-url') || '',
        count: parseInt(chip.getAttribute('data-gf-count'), 10) || 0,
        label: chip.getAttribute('data-gf-label') || '',
        title: chip.getAttribute('data-gf-title') || '',
        gte: chip.getAttribute('data-gf-gte') || '',
        lte: chip.getAttribute('data-gf-lte') || ''
      };
    }
    chip.setAttribute('aria-pressed', 'true');
    scheduleAdvance(root);
  }

  function scheduleAdvance(root) {
    if (root.__gfTimer) { clearTimeout(root.__gfTimer); }
    var delay = prefersReducedMotion() ? 0 : 220;
    root.__gfTimer = setTimeout(function () { advanceFrom(root); }, delay);
  }

  function advanceFrom(root) {
    var state = getState(root);
    if (typeof state.step !== 'number') return;
    if (state.step >= state.totalSteps) { finish(root); }
    else { goToStep(root, state.step + 1); }
  }

  /* ---- Navigation between steps ---- */
  function goToStep(root, target, doFocus, animate) {
    var state = getState(root);
    var stage = root.querySelector('[data-gf-stage]');
    var fromH = stage ? stage.offsetHeight : 0;
    var steps = stepEls(root);
    var i;
    for (i = 0; i < steps.length; i++) {
      steps[i].hidden = steps[i].getAttribute('data-gf-step') !== String(target);
    }
    var stepNum = parseInt(target, 10);
    state.step = stepNum;
    var back = root.querySelector('[data-gf-back]');
    if (back) back.hidden = stepNum <= 1;
    updateProgress(root, stepNum);
    if (animate !== false) animateStageHeight(root, stage, fromH, target);
    if (doFocus !== false) focusStep(root, target);
  }

  // Animate the stage height from its previous size to the incoming step's size,
  // so switching between a tall step (occasions) and a short one (budget) glides
  // instead of snapping the card and the content below it.
  function animateStageHeight(root, stage, fromH, target) {
    if (!stage || prefersReducedMotion()) return;
    var active = stepEl(root, target);
    if (!active) return;
    var to = active.offsetHeight;
    if (!to) return;
    stage.style.overflow = 'hidden';
    stage.style.height = fromH + 'px';
    void stage.offsetHeight; // force reflow so the next height transitions
    stage.style.height = to + 'px';
    if (stage.__gfHTimer) { clearTimeout(stage.__gfHTimer); }
    stage.__gfHTimer = setTimeout(function () {
      stage.style.height = '';
      stage.style.overflow = '';
    }, 340);
  }

  function focusStep(root, target) {
    var el = stepEl(root, target);
    if (!el) return;
    var q = el.querySelector('.gf__q');
    if (q && typeof q.focus === 'function') { q.focus({ preventScroll: false }); }
  }

  function updateProgress(root, stepNum) {
    var state = getState(root);
    var bar = root.querySelector('[data-gf-progress-bar]');
    var label = root.querySelector('[data-gf-progress-label]');
    var status = root.querySelector('[data-gf-status]');
    var pct = Math.round((stepNum / state.totalSteps) * 100);
    if (bar) bar.style.width = pct + '%';
    var msg = 'Step ' + stepNum + ' of ' + state.totalSteps;
    if (label) label.textContent = msg;
    if (status) status.textContent = msg;
  }

  function backStep(root) {
    if (root.__gfTimer) { clearTimeout(root.__gfTimer); }
    var state = getState(root);
    if (state.step > 1) goToStep(root, state.step - 1);
  }

  /* ---- Resolver ---- */
  function resolve(root) {
    var state = getState(root);
    var sel = state.sel;
    var recip = sel.recipient, occ = sel.occasion, bud = sel.budget;
    var dest = null;
    if (recip && occ && state.combos[recip.value + '|' + occ.value]) {
      var c = state.combos[recip.value + '|' + occ.value];
      dest = { url: c.u };
    } else {
      var first = state.priority === 'recipient' ? recip : occ;
      var second = state.priority === 'recipient' ? occ : recip;
      var pick = first || second;
      if (pick) dest = { url: pick.url };
    }
    if (!dest || !dest.url) {
      dest = { url: root.getAttribute('data-gf-default-url') || '/collections/all' };
    }
    var href = dest.url;
    var q = [];
    if (bud) {
      if (bud.gte) q.push(state.gteParam + '=' + encodeURIComponent(bud.gte));
      if (bud.lte) q.push(state.lteParam + '=' + encodeURIComponent(bud.lte));
    }
    if (q.length) href += (href.indexOf('?') === -1 ? '?' : '&') + q.join('&');
    return { href: href };
  }

  function finish(root) {
    var href = resolve(root).href;
    var bar = root.querySelector('[data-gf-progress-bar]');
    if (bar) bar.style.width = '100%';
    var status = root.querySelector('[data-gf-status]');
    if (status) status.textContent = 'Taking you to your gifts…';
    // In the theme editor, don't navigate the editing iframe away.
    if (window.Shopify && window.Shopify.designMode) { window.open(href, '_blank'); return; }
    window.location.assign(href);
  }

  /* ---- Keyboard: roving focus within an options group ---- */
  function handleArrow(e, chip) {
    var group = chip.closest('.gf__options');
    if (!group) return;
    var chips = Array.prototype.slice.call(group.querySelectorAll('[data-gf-opt]'));
    var idx = chips.indexOf(chip);
    if (idx === -1) return;
    var nextIdx = idx;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextIdx = idx + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') nextIdx = idx - 1;
    else return;
    if (nextIdx < 0) nextIdx = chips.length - 1;
    if (nextIdx >= chips.length) nextIdx = 0;
    e.preventDefault();
    chips[nextIdx].focus();
  }

  /* ---- Delegated events ---- */
  function onDocClick(e) {
    var root = e.target.closest('[data-gf-root]');
    if (!root) return;
    var chip = e.target.closest('[data-gf-opt]');
    if (chip && root.contains(chip)) { e.preventDefault(); selectChip(root, chip); return; }
    if (e.target.closest('[data-gf-back]')) { e.preventDefault(); backStep(root); return; }
  }

  function onDocKeydown(e) {
    var chip = e.target.closest && e.target.closest('[data-gf-opt]');
    if (chip && chip.closest('[data-gf-root]')) handleArrow(e, chip);
  }

  /* ---- Init ---- */
  function initRoot(root, force) {
    if (root.__gfInit && !force) return;
    root.__gfInit = true;
    if (root.__gfTimer) { clearTimeout(root.__gfTimer); }
    root.__gfState = null;
    getState(root);
    goToStep(root, 1, false, false);
    prefillFromQuery(root);
  }

  /* TGGCore: deep-linked step 1 — ?recipient=<collection-handle> pre-selects
     that chip and advances, so homepage/email links land mid-quiz. */
  function prefillFromQuery(root) {
    if (root.__gfPrefilled || !window.URLSearchParams) return;
    root.__gfPrefilled = true;
    var value;
    try { value = new URLSearchParams(window.location.search).get('recipient'); } catch (e) { return; }
    if (!value) return;
    var chip = root.querySelector('[data-gf-opt="recipient"][data-gf-value="' + value.replace(/["\\]/g, '') + '"]');
    if (chip) selectChip(root, chip);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-gf-root]');
    for (var i = 0; i < roots.length; i++) initRoot(roots[i], false);
  }

  function onSectionLoad(e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-gf-root]') : null;
    if (root) initRoot(root, true);
  }

  // Bind document-level listeners exactly once, even when the section's <script>
  // executes more than once (multiple finders on a page, or editor reloads).
  if (!window.__tggGiftFinderBound) {
    window.__tggGiftFinderBound = true;
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onDocKeydown);
    document.addEventListener('shopify:section:load', onSectionLoad);
    if (window.InstantClick && typeof window.InstantClick.on === 'function') {
      window.InstantClick.on('change', function () { initAll(); });
    }
  }

  initAll();
})();
